import { AsyncPipe, DatePipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SecurityContext,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AssetDto, DisplayUser, GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { Observable, of, shareReplay, switchMap } from 'rxjs';
import { billingScopes } from '../../../billing-consts';
import { GROWTHBOOK } from '../../../growthbook';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { fixContentEditableStyles } from '../../../lib/fix-contenteditable-styles';
import { sleep } from '../../../lib/sleep';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { AssetService } from '../../../services/asset.service';
import { CleanUserGeneratedHtmlService } from '../../../services/clean-user-generated-html.service';
import { ExifService, NormalizedExif } from '../../../services/exif.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { TagService } from '../../../services/tag.service';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ClickTrackDirective } from '../../../shared/directives/click-track.directive';
import { DataUnitsBigIntPipe } from '../../../shared/pipes/data-units-big-int.pipe';
import { FloorPipe } from '../../../shared/pipes/floor.pipe';
import { MegapixelsPipe } from '../../../shared/pipes/megapixels.pipe';
import { VideoResolutionTypePipe } from '../../../shared/pipes/video-resolution-type.pipe';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { AssetTagsComponent } from '../asset-tags/asset-tags.component';
import { AssetVersionMenuComponent } from '../asset-version-menu/asset-version-menu.component';

@Component({
  selector: 'app-asset-info',
  templateUrl: './asset-info.component.html',
  styleUrls: ['./asset-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    WysiwygDirective,
    ButtonDirective,
    SvgComponent,
    AssetTagsComponent,
    ContextMenuComponent,
    AssetVersionMenuComponent,
    UpperCasePipe,
    DatePipe,
    MegapixelsPipe,
    BillingScopePipe,
    ClickTrackDirective,
    FloorPipe,
    VideoResolutionTypePipe,
    DecimalPipe,
    AsyncPipe,
    DataUnitsBigIntPipe,
    AvatarComponent,
  ],
})
export class AssetInfoComponent implements OnChanges {
  private exifService = inject(ExifService);
  private galleryService = inject(GalleryService);
  private state = inject(StateService);
  private assetService = inject(AssetService);
  private _sanitizer = inject(DomSanitizer);
  private cleanUserGeneratedHtmlService = inject(CleanUserGeneratedHtmlService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private tagService = inject(TagService);
  private permissionService = inject(PermissionService);
  private check = inject(ChangeDetectorRef);
  @ViewChild('description', { static: false }) description: ElementRef<HTMLDivElement>;
  @ViewChild('descriptionInput', { static: false }) descriptionInput: ElementRef<HTMLDivElement>;
  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() accessLevel: GalleryAccessLevel;
  tenant: TenantDto;
  exif: NormalizedExif;
  allowVersionHistory: boolean;
  edittingDescription = signal(false);
  expandedDescription = signal(false);
  newAssetDescription: string;
  descriptionTooBig = signal(false);
  assetDescription = signal('');
  tenantTags$ = this.tagService.tags$;
  creator$: Observable<DisplayUser>;

  public growthBook = inject(GROWTHBOOK);

  private resetDescription() {
    this.assetDescription.set(
      this.cleanUserGeneratedHtmlService.clean(this.asset.description || this.exif?.description || '')
    );
    this.newAssetDescription = this.assetDescription() || '';
    if (this.assetDescription() === '\n') this.assetDescription.set('');
    this.assetDescription.set(this._sanitizer.sanitize(SecurityContext.NONE, this.assetDescription()));
  }

  async ngOnChanges() {
    this.exif = this.exifService.getExif(this.asset);
    this.tenant = (this.gallery.tenant as TenantDto)?.id
      ? (this.gallery.tenant as TenantDto)
      : this.state.activeTenant$?.value;

    this.resetDescription();

    this.allowVersionHistory = this.gallery.version_history_enabled || this.accessLevel === 'edit';
    if (!this.permissionService.canDisplayElement(billingScopes.assetVersion.multiple)) {
      this.allowVersionHistory = false;
    }

    this.creator$ = this.getCreator();
    this.check.detectChanges();
    await sleep(0);
    this.calcDescriptionTooBig();
  }

  async saveDescription() {
    this.asset.description = fixContentEditableStyles(this.newAssetDescription);
    this.edittingDescription.set(false);

    if (this.asset.description === '') this.asset.description = '\n';
    if (this.asset.description === '/auto') this.asset.description = '';

    this.analytics.track('save_description');
    this.check.detectChanges();

    this.ngOnChanges();
    this.calcDescriptionTooBig();

    await this.assetService.updateAsset(
      this.asset,
      { description: this.asset.description },
      <string>this.asset.gallery,
      <string>this.asset.section
    );
  }

  async cancelEditDescription() {
    this.resetDescription();
    this.edittingDescription.set(false);
    await sleep(0);
    this.calcDescriptionTooBig();
  }

  async expandDescription() {
    this.expandedDescription.set(true);
    await sleep(0);
    this.calcDescriptionTooBig();
  }

  async shrinkDescription() {
    this.expandedDescription.set(false);
    await sleep(0);
    this.calcDescriptionTooBig();
    this.description.nativeElement.scrollTop = 0;
  }

  async startEditing(isNew = false) {
    this.edittingDescription.set(true);
    await sleep(0);
    this.descriptionInput?.nativeElement.focus();
    if (isNew) {
      this.analytics.track('add_description');
    } else {
      this.analytics.track('edit_description');
    }
  }

  convertFrameRate(frameRate: string) {
    if (!frameRate) return 30;
    const [numerator, denominator] = frameRate.toString().split('/');
    if (!denominator) return Number(numerator);
    return Math.round(parseInt(numerator) / parseInt(denominator));
  }

  private calcDescriptionTooBig() {
    if (!this.description) return;
    this.descriptionTooBig.set(
      this.description.nativeElement.clientHeight < this.description.nativeElement.scrollHeight
    );
  }

  private getCreator(): Observable<DisplayUser> {
    return this.assetService.getCreator(this.asset.version as string, this.gallery.id).pipe(
      switchMap(creator => {
        if (creator === null) {
          return this.galleryService.getGalleryOwner(
            this.gallery.id,
            !this.state.tenants?.some(t => t.id === this.tenant.id)
          );
        }
        return of(creator);
      }),
      shareReplay(1)
    );
  }
}
